import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import IconButton from '@mui/material/IconButton';
import Autocomplete from '@mui/material/Autocomplete';
import Divider from '@mui/material/Divider';
import ToggleButton from '@mui/material/ToggleButton';
import Tooltip from '@mui/material/Tooltip';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';

import StaticAdsService from 'services/StaticAdsService';

import formatEnumLabel from './formatEnumLabel';
import ImportStaticsDialog from './ImportStaticsDialog';
import AnalyzeDialog, { AnalyzeRequest } from './AnalyzeDialog';
import GenerateDialog from './GenerateDialog';
import StaticAdCard from './StaticAdCard';
import StaticAdDetailDialog from './StaticAdDetailDialog';
import StaticAdTable from './StaticAdTable';
import UploadStaticDialog from './UploadStaticDialog';
import useStaticPipeline from './useStaticPipeline';
import { isStaticAdBusy, staticStatusLabel } from './staticStatus';
import { STATIC_SORT_OPTIONS, sanitizeStaticSortField, staticComparators } from './staticSort';
import { StaticAdFacets, StaticAdFilters, StaticAdSummary } from './types';

const PAGE_SIZES = [24, 48, 96];

// Which view someone prefers is a lasting preference, not per-visit state.
const VIEW_STORAGE_KEY = 'sbly.staticLibrary.view';
const SORT_FIELD_STORAGE_KEY = 'sbly.staticLibrary.sortField';
const SORT_DIR_STORAGE_KEY = 'sbly.staticLibrary.sortDir';

// Which facet drives which filter key. One list so the row of dropdowns and the
// query params can't drift apart.
const FACET_FILTERS: Array<{ key: keyof StaticAdFilters; facet: keyof StaticAdFacets; label: string }> = [
  { key: 'staticFormat', facet: 'staticFormats', label: 'Format' },
  { key: 'visualHook', facet: 'visualHooks', label: 'Visual hook' },
  { key: 'layout', facet: 'layouts', label: 'Layout' },
  { key: 'textDensity', facet: 'textDensities', label: 'Text' },
  { key: 'aspectRatio', facet: 'aspectRatios', label: 'Ratio' },
  { key: 'pageName', facet: 'pageNames', label: 'Advertiser' },
  { key: 'keyword', facet: 'keywords', label: 'Keyword' },
  { key: 'pipelineStatus', facet: 'pipelineStatuses', label: 'Status' },
];

export default function StaticLibrary() {
  const service = useMemo(() => new StaticAdsService(), []);

  const [ads, setAds] = useState<StaticAdSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState<StaticAdFacets | null>(null);
  const [filters, setFilters] = useState<StaticAdFilters>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const [openAdId, setOpenAdId] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [view, setView] = useState<'grid' | 'table'>(() => (
    (localStorage.getItem(VIEW_STORAGE_KEY) as 'grid' | 'table') || 'grid'
  ));
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [analyzeOpen, setAnalyzeOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);

  // Requests can overlap (typing a search while a page change is in flight);
  // only the NEWEST request may write state, or a slow stale response
  // overwrites the fresh page after it lands.
  const loadSeq = useRef(0);
  // Last known pipeline status of every ad EVER seen this session, not just
  // the current page — a selection can span pages, and judging "already
  // analyzed?" from the visible page alone misreads off-page selections.
  const statusById = useRef<Map<string, string>>(new Map());
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    const resp = await service.listAds(filters, { limit: pageSize, offset: page * pageSize });
    if (seq !== loadSeq.current) return;
    if (resp) {
      resp.ads.forEach(ad => statusById.current.set(ad.staticAdId, ad.pipelineStatus));
      setAds(resp.ads);
      setTotal(resp.total);
      setLoadError(false);
    } else {
      setLoadError(true);
    }
    setLoading(false);
  }, [service, filters, page, pageSize]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    let active = true;
    void service.getFacets().then((resp) => { if (active && resp) setFacets(resp); });
    // Tags are not a facet of the ads table — they live in the shared tag store —
    // so they load alongside rather than inside the facets call.
    void service.getTags().then((resp) => { if (active && resp) setAllTags(resp.tags); });
    return () => { active = false; };
  }, [service]);

  // Poll only the ads that are actually unfinished. A library of processed ads
  // polls nothing at all.
  const unfinishedIds = useMemo(
    () => ads
      .filter(ad => isStaticAdBusy(ad.pipelineStatus))
      .map(ad => ad.staticAdId),
    [ads],
  );
  const { statuses } = useStaticPipeline(service, unfinishedIds, load);

  const applyFilter = (key: keyof StaticAdFilters, value: string) => {
    setPage(0);
    setFilters(prev => {
      const next = { ...prev };
      if (value) (next[key] as string) = value;
      else delete next[key];
      return next;
    });
  };

  const submitSearch = () => {
    const q = search.trim();
    // Blur fires this too — an unchanged query must be a no-op, or clicking
    // anywhere after focusing the field resets the page and refires the load.
    if ((filters.q || '') === q) return;
    setPage(0);
    setFilters(prev => {
      const next = { ...prev };
      if (q) next.q = q;
      else delete next.q;
      return next;
    });
  };

  const activeFilterCount = Object.keys(filters).length;

  // Sorting lives here rather than inside the table so the grid and the table
  // share one order — switching views must not silently reshuffle the ads.
  // Newest fetched first by default: the reason to open this page is usually
  // "what did we just pull in".
  // Sanitized on init: a stale persisted key (from a renamed or removed
  // field) has no comparator and would leave the library silently unsorted.
  const [sortField, setSortField] = useState<string>(
    () => sanitizeStaticSortField(localStorage.getItem(SORT_FIELD_STORAGE_KEY)),
  );
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(
    () => (localStorage.getItem(SORT_DIR_STORAGE_KEY) as 'asc' | 'desc') || 'desc',
  );

  const applySort = (field: string, dir: 'asc' | 'desc') => {
    setSortField(field);
    setSortDir(dir);
    try {
      localStorage.setItem(SORT_FIELD_STORAGE_KEY, field);
      localStorage.setItem(SORT_DIR_STORAGE_KEY, dir);
    } catch { /* private mode */ }
  };

  // Clicking a table header toggles direction on the active column, same as before.
  const handleSort = (field: string) => {
    applySort(field, sortField === field && sortDir === 'asc' ? 'desc' : 'asc');
  };

  const comparators = useMemo(() => staticComparators(sortDir), [sortDir]);
  const sortedAds = useMemo(() => {
    const cmp = comparators[sortField];
    if (!cmp) return ads;
    // Sign-flipped rather than reversed: the comparators put missing values
    // last in BOTH directions, and reversing the finished array would drag
    // every ungraded ad back to the top.
    return [...ads].sort((a, b) => (sortDir === 'asc' ? cmp(a, b) : -cmp(a, b)));
  }, [ads, comparators, sortField, sortDir]);

  const chooseView = (next: 'grid' | 'table') => {
    setView(next);
    try { localStorage.setItem(VIEW_STORAGE_KEY, next); } catch { /* private mode */ }
  };

  const toggleSelect = (staticAdId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(staticAdId)) next.delete(staticAdId);
      else next.add(staticAdId);
      return next;
    });
  };

  // Select-all applies to the CURRENT PAGE only. Selecting rows the user can't
  // see and then re-analyzing them would be a nasty surprise on a 500-ad library.
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const allOnPage = ads.every(a => prev.has(a.staticAdId));
      if (allOnPage) return new Set();
      return new Set(ads.map(a => a.staticAdId));
    });
  };

  // Distinct from loadError: the list on screen is fine, an ACTION on it
  // failed. Dismissible because the list stays usable underneath.
  const [actionError, setActionError] = useState<string | null>(null);
  // Not a failure — an action that partly did nothing, which the operator has
  // to know about before deciding to run it again.
  const [batchNotice, setBatchNotice] = useState<string | null>(null);

  const batchAnalyze = async (req: AnalyzeRequest) => {
    setBatchBusy(true);
    setBatchNotice(null);
    try {
      // A rejection propagates to the AnalyzeDialog, which stays open and
      // shows it — flagging it as a LOAD error here mislabeled the failure
      // and quietly closed the dialog as if the run had been queued.
      const resp = await service.batchAnalyze([...selected], req);
      // Only 'started' actually queued a run. 'skipped' (the ad was mid-analysis
      // so it wasn't requeued) and 'already_running' (a job for it was already
      // on the queue) mean the click did nothing for that ad — reported as
      // "queued them all", the operator re-runs and pays for it.
      // Note the count is of ADS, not of the selection: the backend collapses
      // several images of one ad to a single analysis.
      const results = resp?.results || [];
      const untouched = results.filter(r => r.status !== 'started').length;
      if (untouched) {
        setBatchNotice(`Queued ${results.length - untouched} of ${results.length} — ${untouched} `
          + `${untouched === 1 ? 'was' : 'were'} already being analyzed and ${untouched === 1 ? 'was' : 'were'} left to finish.`);
      }
      setSelected(new Set());
      await load();
    } finally {
      setBatchBusy(false);
    }
  };

  // "Select all" means everything currently listed, so its label has to track
  // the page you are looking at rather than the whole library.
  const allOnPageSelected = sortedAds.length > 0 && sortedAds.every(a => selected.has(a.staticAdId));

  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  // Confirmed (a real dialog, matching the Output tab) because it is a bulk
  // action on things you cannot see all of — the selection can span pages.
  // The delete itself is a soft delete on the backend, so ads stay recoverable.
  const batchRemove = async () => {
    const ids = [...selected];
    setConfirmRemoveOpen(false);
    setBatchBusy(true);
    setActionError(null);
    try {
      // Settled, not all-or-nothing: one failed delete must not hide that the
      // rest succeeded, nor let the failures pass as removed.
      const results = await Promise.allSettled(ids.map(id => service.deleteAd(id)));
      const failedIds = ids.filter((_, i) => results[i].status === 'rejected');
      if (failedIds.length) {
        setActionError(`Removed ${ids.length - failedIds.length} of ${ids.length} ads — ${failedIds.length} failed. The failed ones stay selected.`);
        console.error('Batch remove failures', results.filter(r => r.status === 'rejected'));
      }
      setSelected(new Set(failedIds));
      await load();
    } finally {
      setBatchBusy(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_e, next) => next && chooseView(next)}
        >
          <ToggleButton value="grid" aria-label="Card grid"><ViewModuleIcon fontSize="small" /></ToggleButton>
          <ToggleButton value="table" aria-label="Table"><ViewListIcon fontSize="small" /></ToggleButton>
        </ToggleButtonGroup>

        {/* The table sorts by clicking a header; the grid has no headers, so it
            gets the same sort as an explicit control. Both drive one state. */}
        {view === 'grid' && (
          <>
            <TextField
              select
              size="small"
              label="Sort by"
              value={sortField}
              onChange={e => applySort(e.target.value, sortDir)}
              sx={{ minWidth: 160 }}
            >
              {STATIC_SORT_OPTIONS.map(opt => (
                <MenuItem key={opt.field} value={opt.field}>{opt.label}</MenuItem>
              ))}
            </TextField>
            <Tooltip title={sortDir === 'desc' ? 'Highest / newest first' : 'Lowest / oldest first'}>
              <IconButton
                size="small"
                aria-label="Reverse sort order"
                onClick={() => applySort(sortField, sortDir === 'asc' ? 'desc' : 'asc')}
              >
                {sortDir === 'desc' ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </>
        )}
        {/* Honesty about client-side sorting: the server pages by scrapedAt,
            so any other sort only reorders the page on screen. */}
        {total > pageSize && sortField !== 'scrapedAt' && (
          <Typography variant="caption" color="text.secondary">
            Sorting applies to this page
          </Typography>
        )}
        {/* Selection actions live here permanently, disabled when nothing is
            selected. As a bar that appeared on first selection they pushed the
            whole grid down, so the tile you had just clicked jumped out from
            under the cursor. */}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Button size="small" onClick={toggleSelectAll} disabled={!sortedAds.length}>
          {allOnPageSelected ? 'Deselect all' : 'Select all'}
        </Button>
        <Typography
          variant="body2"
          color={selected.size ? 'text.primary' : 'text.disabled'}
          // Fixed width so the count changing from 1 to 10 doesn't nudge the
          // buttons beside it.
          sx={{ minWidth: 78 }}
        >
          {selected.size} selected
        </Typography>
        <Button size="small" disabled={!selected.size || batchBusy} onClick={() => setAnalyzeOpen(true)}>
          {batchBusy ? 'Working…' : 'Analyze'}
        </Button>
        <Button size="small" variant="outlined" disabled={!selected.size || batchBusy} onClick={() => setGenerateOpen(true)}>
          Generate
        </Button>
        <Button size="small" color="error" disabled={!selected.size || batchBusy} onClick={() => setConfirmRemoveOpen(true)}>
          Remove
        </Button>
        <Button size="small" disabled={!selected.size} onClick={() => setSelected(new Set())}>Clear</Button>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
        <Button size="small" variant="outlined" startIcon={<CloudDownloadOutlinedIcon />} onClick={() => setImportOpen(true)}>
          Fetch from Ad Library
        </Button>
        <Button size="small" variant="contained" startIcon={<AddPhotoAlternateOutlinedIcon />} onClick={() => setUploadOpen(true)}>
          Upload
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {/* Search sits with the filters because that is what it is — one more
              way of narrowing the same list. */}
          <TextField
            size="small"
            label="Search"
            placeholder="On-image text, headline, advertiser"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submitSearch(); }}
            onBlur={submitSearch}
            sx={{ minWidth: 260 }}
          />
          {FACET_FILTERS.map(({ key, facet, label }) => {
            const options = facets?.[facet] ?? [];
            return (
              <TextField
                key={key}
                select
                size="small"
                label={label}
                value={(filters[key] as string) || ''}
                onChange={e => applyFilter(key, e.target.value)}
                sx={{ minWidth: 150 }}
                disabled={!options.length}
              >
                <MenuItem value="">Any</MenuItem>
                {options.map(option => (
                  <MenuItem key={option} value={option}>
                    {/* Keywords are the user's own search terms; running them
                        through the enum formatter would retitle their words.
                        Pipeline statuses get the SAME words the cards and the
                        detail dialog use — the formatter turned DONE into
                        "Done" beside a card reading "Analyzed". */}
                    {key === 'keyword' ? option
                      : key === 'pipelineStatus' ? staticStatusLabel(option)
                        : formatEnumLabel(option)}
                  </MenuItem>
                ))}
              </TextField>
            );
          })}
          {/* Multi-select: several tags widen the result to ads carrying any of
              them, which is how you sweep up a theme spread across labels. */}
          <Autocomplete
            multiple
            size="small"
            options={allTags}
            value={selectedTags}
            onChange={(_e, next) => {
              setSelectedTags(next);
              setPage(0);
              setFilters(prev => {
                const out = { ...prev };
                if (next.length) out.tag = next; else delete out.tag;
                return out;
              });
            }}
            sx={{ minWidth: 220 }}
            renderInput={params => <TextField {...params} label="Tags" placeholder={selectedTags.length ? '' : 'Any'} />}
          />

          {activeFilterCount > 0 && (
            <Chip
              size="small"
              label={`Clear ${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'}`}
              onDelete={() => { setFilters({}); setSearch(''); setSelectedTags([]); setPage(0); }}
              onClick={() => { setFilters({}); setSearch(''); setSelectedTags([]); setPage(0); }}
            />
          )}
        </Stack>
      </Paper>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn&apos;t load the library. Try Refresh — if it keeps failing the API may be down.
        </Alert>
      )}

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {batchNotice && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setBatchNotice(null)}>
          {batchNotice}
        </Alert>
      )}

      {loading && !ads.length && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      )}

      {!loading && !ads.length && !loadError && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" gutterBottom>No static ads yet.</Typography>
          <Typography variant="body2" color="text.secondary">
            Upload an image you found, or fetch a keyword from the Facebook Ad Library.
          </Typography>
        </Paper>
      )}

      {!!ads.length && (
        <>
          {view === 'grid' ? (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 2,
              opacity: loading ? 0.6 : 1,
            }}>
              {sortedAds.map(ad => (
                <StaticAdCard
                  key={ad.staticAdId}
                  ad={ad}
                  status={statuses[ad.staticAdId]}
                  onOpen={setOpenAdId}
                  selected={selected.has(ad.staticAdId)}
                  onToggleSelect={toggleSelect}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ opacity: loading ? 0.6 : 1 }}>
              <StaticAdTable
                ads={sortedAds}
                sortField={sortField}
                sortDir={sortDir}
                onSort={handleSort}
                statuses={statuses}
                selected={selected}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                onOpen={setOpenAdId}
              />
            </Box>
          )}

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, next) => setPage(next)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={PAGE_SIZES}
            onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
          />
        </>
      )}
      <GenerateDialog
        open={generateOpen}
        staticAdIds={[...selected]}
        service={service}
        onClose={() => setGenerateOpen(false)}
        onQueued={() => setSelected(new Set())}
      />

      <AnalyzeDialog
        open={analyzeOpen}
        count={selected.size}
        // Only a re-run if EVERY selected ad is KNOWN to be analyzed. Selected
        // ids from other pages may have no known status — then it's honestly
        // unknown, not silently "fresh".
        isRerun={selected.size > 0
          && [...selected].every(id => statusById.current.get(id) === 'DONE')}
        statusUnknown={[...selected].some(id => !statusById.current.has(id))}
        service={service}
        onClose={() => setAnalyzeOpen(false)}
        onConfirm={batchAnalyze}
      />


      <StaticAdDetailDialog
        service={service}
        staticAdId={openAdId}
        onClose={() => setOpenAdId(null)}
        onChanged={load}
        onOpenOther={setOpenAdId}
      />
      <UploadStaticDialog
        service={service}
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={load}
      />
      <ImportStaticsDialog
        service={service}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onFinished={load}
      />
      <Dialog open={confirmRemoveOpen} onClose={() => setConfirmRemoveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove {selected.size} ad{selected.size === 1 ? '' : 's'} from the library?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Their analyses and any creatives generated from them are not affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemoveOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={batchRemove}>Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
